import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Alert, Button, FormControlLabel, Switch,
} from '@mui/material';
import { parseISO, format } from 'date-fns';
import { leaveRequestService, leaveBalanceService } from '../../services/api';

const isSunday = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).getDay() === 0;
};

const todayStr = () => new Date().toISOString().split('T')[0];

// Leave types that draw from the shared balance pool — kept in sync with
// LeaveRequest.BalanceTrackedTypes on the backend. Comp Off Leave deducts from the same pool
// as Earned Leave (a manager manually credits Earned Leave when someone works a comp-off day).
// Transfer/Paternity/Bereavement Leave are paid too, but have no balance to check or show here.
const BALANCE_TRACKED_TYPES = ['Sick Leave', 'Earned Leave', 'Comp Off Leave'];

// A plain date string's own-day boundary, for the overlap check below — parseISO on a bare
// 'yyyy-MM-dd' stays at local midnight, same as the backend's own date-only comparison.
const asDate = (dateStr: string) => parseISO(dateStr.split('T')[0]);

// Leave amounts are stored with 2 decimal places (DECIMAL(8,2)); round JS arithmetic to match
// so e.g. 2 - 1.6 shows as 0.4, not 0.3999999999999999.
const round2 = (n: number) => Math.round(n * 100) / 100;

export interface RequestLeaveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful submit, with the server's confirmation message. */
  onSubmitted?: (message: string) => void;
  /** Pre-fills both Start and End Date — e.g. when opened as a shortcut for one specific
   * attendance-grid row. Defaults to today when omitted (the plain "Request Leave" button). */
  initialDate?: string;
  /** The employee's own existing leave requests — used for an immediate "you already have a
   * request for these dates" warning before even calling the API. The backend independently
   * re-validates this (see LeaveRequestsController.CreateLeaveRequest), so a stale/missing list
   * here only means the warning doesn't show up front, not that the duplicate goes through. */
  existingLeaveRequests?: any[];
}

// Shared "Request Leave" dialog — used both from the full Leave Requests page (LeaveManagement)
// and as a per-date shortcut from the attendance grid (AttendanceTracking), so both open the
// exact same form instead of drifting into two slightly different leave-request UIs.
export const RequestLeaveDialog: React.FC<RequestLeaveDialogProps> = ({ open, onClose, onSubmitted, initialDate, existingLeaveRequests }) => {
  const [balance, setBalance] = useState<any>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    startDate: initialDate || todayStr(),
    endDate: initialDate || todayStr(),
    leaveType: 'Sick Leave',
    dayType: 'Full',
    reason: '',
  });

  // Re-seed the form to this open's date every time the dialog opens, so a leftover date from
  // a previous open (or a different row's shortcut) never lingers.
  useEffect(() => {
    if (!open) return;
    setFormData({
      startDate: initialDate || todayStr(),
      endDate: initialDate || todayStr(),
      leaveType: 'Sick Leave',
      dayType: 'Full',
      reason: '',
    });
    setError('');
    leaveBalanceService.getMyBalance().then(setBalance).catch(() => setBalance(null));
  }, [open, initialDate]);

  // Half day only makes sense for a single date — same rule the backend enforces
  // (LeaveRequestsController.CreateLeaveRequest).
  const isSingleDate = formData.startDate === formData.endDate;

  // Live preview of the Paid/LWP split, mirroring LeaveRequestsController.CreateLeaveRequest's
  // own logic: cover as many days as the current balance allows (chronologically from the
  // start), the rest previews as Unpaid (LWP). This is just a preview for the employee before
  // submitting — the backend recomputes and is authoritative (balance may have moved since this
  // was fetched, e.g. another pending request of theirs got approved in the meantime).
  const totalDays = (() => {
    if (formData.dayType === 'Half') return 0.5;
    const diff = Math.round((asDate(formData.endDate).getTime() - asDate(formData.startDate).getTime()) / 86400000) + 1;
    return diff > 0 ? diff : 0;
  })();
  const isBalanceTracked = BALANCE_TRACKED_TYPES.includes(formData.leaveType);
  const previewPaidDays = balance && isBalanceTracked ? round2(Math.max(0, Math.min(balance.totalBalance, totalDays))) : null;
  const previewUnpaidDays = previewPaidDays !== null ? round2(totalDays - previewPaidDays) : null;

  const reasonWordCount = formData.reason.trim().split(/\s+/).filter(Boolean).length;
  const reasonValid = reasonWordCount >= 2;

  // Same-dates duplicate check, done here for an immediate message instead of a round trip —
  // the backend enforces this independently against the DB regardless of what this list says.
  const newStart = asDate(formData.startDate);
  const newEnd = asDate(formData.endDate);
  const overlapping = (existingLeaveRequests || []).find(l =>
    (l.status === 'Pending' || l.status === 'Approved') &&
    asDate(l.startDate) <= newEnd && asDate(l.endDate) >= newStart
  );
  const overlapWarning = overlapping
    ? `You already have a ${overlapping.status.toLowerCase()} leave request from ` +
      `${format(asDate(overlapping.startDate), 'MMM dd, yyyy')} to ${format(asDate(overlapping.endDate), 'MMM dd, yyyy')} ` +
      'that overlaps these dates.'
    : '';

  const handleSubmit = async () => {
    if (!reasonValid) return setError('Please provide a reason for leave (at least 2 words).');
    if (isSunday(formData.startDate) || isSunday(formData.endDate)) {
      return setError('Sunday is a weekly off. Leave cannot start or end on a Sunday.');
    }
    if (overlapWarning) return setError(overlapWarning);

    setSubmitting(true);
    try {
      const created = await leaveRequestService.createLeaveRequest(formData);
      const createdTotal = created?.dayType === 'Half'
        ? 0.5
        : Math.round((asDate(created.endDate).getTime() - asDate(created.startDate).getTime()) / 86400000) + 1;
      const createdPaid = created?.paidDays ?? createdTotal;
      onSubmitted?.(
        createdPaid <= 0 && formData.leaveType !== 'Leave Without Pay'
          ? 'Leave request submitted as Unpaid (LWP) — insufficient balance for the selected leave type'
          : createdPaid < createdTotal
          ? `Leave request submitted — ${round2(createdPaid)} day(s) Paid, ${round2(createdTotal - createdPaid)} day(s) Unpaid (LWP)`
          : 'Leave request submitted successfully'
      );
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error submitting leave request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Leave</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
        {!error && overlapWarning && <Alert severity="warning" sx={{ mb: 1 }}>{overlapWarning}</Alert>}
        <TextField
          fullWidth
          label="Start Date"
          type="date"
          value={formData.startDate}
          onChange={(e) => setFormData((f) => ({
            ...f,
            startDate: e.target.value,
            // Half day only applies to a single date — switching to a range clears it.
            dayType: e.target.value === f.endDate ? f.dayType : 'Full',
          }))}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          fullWidth
          label="End Date"
          type="date"
          value={formData.endDate}
          onChange={(e) => setFormData((f) => ({
            ...f,
            endDate: e.target.value,
            dayType: f.startDate === e.target.value ? f.dayType : 'Full',
          }))}
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Leave Type</InputLabel>
          <Select
            value={formData.leaveType}
            onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
            label="Leave Type"
          >
            <MenuItem value="Sick Leave">Sick Leave</MenuItem>
            <MenuItem value="Earned Leave">Earned Leave</MenuItem>
            <MenuItem value="Transfer Leave">Transfer Leave</MenuItem>
            <MenuItem value="Paternity Leave">Paternity Leave</MenuItem>
            <MenuItem value="Bereavement Leave">Bereavement Leave</MenuItem>
            <MenuItem value="Comp Off Leave">Comp Off Leave</MenuItem>
            <MenuItem value="Leave Without Pay">Leave Without Pay (LWP)</MenuItem>
          </Select>
        </FormControl>
        {isSingleDate && (
          <FormControlLabel
            sx={{ mt: 1, display: 'block' }}
            control={
              <Switch
                checked={formData.dayType === 'Half'}
                onChange={(e) => setFormData({ ...formData, dayType: e.target.checked ? 'Half' : 'Full' })}
              />
            }
            label="Half Day (consumes 0.5 day instead of 1)"
          />
        )}
        {isBalanceTracked && previewPaidDays !== null && previewUnpaidDays !== null && totalDays > 0 && (
          <Alert severity={previewUnpaidDays > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
            <strong>Total requested:</strong> {totalDays} day(s) &nbsp;•&nbsp;
            <strong>Paid Leave:</strong> {previewPaidDays} &nbsp;•&nbsp;
            <strong>Leave Without Pay:</strong> {previewUnpaidDays} &nbsp;•&nbsp;
            <strong>Balance after:</strong> {round2(Math.max(0, balance.totalBalance - previewPaidDays))}
            {previewUnpaidDays > 0 && (
              <>
                {' '}— {previewPaidDays <= 0 ? 'this request' : `${previewUnpaidDays} of ${totalDays} day(s)`} will
                be submitted as Unpaid (LWP) since available balance is only {balance.totalBalance}.
              </>
            )}
          </Alert>
        )}
        <TextField
          fullWidth
          label="Reason"
          value={formData.reason}
          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
          margin="normal"
          multiline
          rows={3}
          required
          error={formData.reason.length > 0 && !reasonValid}
          helperText={formData.reason.length > 0 && !reasonValid ? 'Please enter at least 2 words.' : ' '}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting || !reasonValid || !!overlapWarning}>
          {submitting ? 'Submitting…' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestLeaveDialog;
